import { fontFamilyBrand, Media, UI } from '@cowprotocol/ui'

import styled, { css, keyframes } from 'styled-components/macro'

import type { CowSpeechBubbleVariant } from './CowSpeechBubble.types'

const fadeUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(22px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const blink = keyframes`
  0%, 49% {
    opacity: 1;
  }
  50%, 100% {
    opacity: 0;
  }
`

export const BubbleRow = styled.div`
  position: absolute;
  top: -55px;
  right: 48%;
  pointer-events: none;
  z-index: 2;

  ${Media.upToLarge()} {
    right: 49%;
    top: -54px;
  }

  ${Media.upToMedium()} {
    right: 46%;
    top: -60px;
  }

  ${Media.upToSmall()} {
    right: 45%;
    top: -55px;
  }

  ${Media.upToExtraSmall()} {
    right: 38%;
  }

  ${Media.upToTiny()} {
    right: 35%;
  }
`

export const Bubble = styled.div<{ $variant: CowSpeechBubbleVariant }>`
  --notificationAnchorHeight: 104px;
  position: relative;
  padding: ${({ $variant }) => ($variant === 'notification' ? '16px' : '16px 20px')};
  background: var(${UI.COLOR_PAPER});
  color: var(${UI.COLOR_TEXT});
  border-radius: 18px 6px 18px 18px;
  box-shadow: 0 16px 36px -20px rgba(0, 0, 0, 0.45);
  max-width: 260px;
  width: ${({ $variant }) => ($variant === 'notification' ? '260px' : 'auto')};
  overflow: visible;
  max-height: min(280px, 50vh);
  text-align: center;
  pointer-events: auto;
  z-index: 2;
  white-space: normal;
  opacity: 0;
  animation: ${({ $variant }) => ($variant === 'notification' ? fadeIn : fadeUp)} 0.45s ease-out forwards;
  will-change: opacity, transform;
  ${({ $variant }) =>
    $variant === 'notification' &&
    css`
      transform: translateY(calc(var(--notificationAnchorHeight) - 100%));
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: ${({ $variant }) =>
      $variant === 'notification' ? 'translateY(calc(var(--notificationAnchorHeight) - 100%))' : 'none'};
  }

  &::after {
    content: '';
    position: absolute;
    right: -22px;
    bottom: 16px;
    border-style: solid;
    border-width: 0 0 22px 22px;
    border-color: transparent transparent transparent var(${UI.COLOR_PAPER});
  }

  ${Media.upToLarge()} {
    max-width: 220px;

    &::after {
      right: -16px;
      bottom: 14px;
      border-width: 0 0 18px 18px;
    }
  }

  ${Media.upToSmall()} {
    padding: 12px 16px;
    max-width: 200px;
    --notificationAnchorHeight: 92px;
  }
`

export const BubbleContent = styled.div<{ $variant: CowSpeechBubbleVariant }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  ${({ $variant }) =>
    $variant === 'hiring'
      ? css`
          padding: 10px;
        `
      : css`
          align-items: stretch;
          max-height: min(226px, 45vh);
          overflow-y: auto;
          padding-right: 4px;
          scrollbar-gutter: stable;
          ${({ theme }) => theme.colorScrollbar};
        `}

  ${Media.upToSmall()} {
    font-size: 14px;
  }
`

export const TypingLine = styled.span`
  display: inline-flex;
  align-items: center;
  ${fontFamilyBrand}
  min-height: 1.4em;
  white-space: pre-wrap;
`

export const Cursor = styled.span<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'inline-block' : 'none')};
  width: 2px;
  height: 1.2em;
  margin-left: 4px;
  background: currentColor;
  animation: ${blink} 1s steps(1, start) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const JobsLink = styled.a<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: currentColor;
  text-decoration: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  transform: translateY(${({ $visible }) => ($visible ? '0' : '4px')});

  &:hover {
    text-decoration: underline;
  }
`

export const Arrow = styled.span`
  font-size: 14px;
  line-height: 1;
`
